import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckIcon from "@mui/icons-material/Check";
import FilterListIcon from "@mui/icons-material/FilterList";
import LiveTvOutlinedIcon from "@mui/icons-material/LiveTvOutlined";
import { Chip } from "@andorhealth/clientuireact.designsystem";
import {
  ConciergeChecking,
  ConciergeUnavailable,
} from "@/components/ConciergeUnavailable";
import { HeaderIconTooltip, StaffAppHeader } from "@/components/StaffAppHeader";
import {
  acceptPatientRequest,
  fetchManagementJson,
  managementTenantKey,
  setStaffTenantFallback,
} from "@/lib/management-api";
import { useAuth } from "@/lib/auth/AuthContext";
import { useConciergeBoardAbly, type AblyLiveStatus } from "@/lib/ably-board";

const PAGE_SIZE = 50;
const REFRESH_MS = 30_000;
/** Success hold + fade/collapse — keep in sync with `ackDismiss` keyframes. */
const ACK_HOLD_MS = 3400;
const ENTER_MS = 650;
const FILTERS_IDLE_MS = 30_000;
const TV_MODE_STORAGE_KEY = "patient-concierge.tv-mode";
const ALL = "";

function readTvModePreference(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const raw = window.localStorage.getItem(TV_MODE_STORAGE_KEY);
    if (raw == null) return true;
    return raw !== "0" && raw !== "false";
  } catch {
    return true;
  }
}

function boardAmbientBg(tvMode: boolean, liveStatus: AblyLiveStatus): string {
  if (!tvMode) return "#e8ecf1";
  // Stronger warm wash so offline reads across the room.
  return liveStatus === "online" ? "#e8ecf1" : "#ead5d0";
}

function BoardItemMotion({
  acknowledging,
  entering,
  gapBottom = 0,
  children,
}: {
  acknowledging: boolean;
  entering?: boolean;
  /** Bottom gap in px while visible (collapses with dismiss). */
  gapBottom?: number;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        overflow: "hidden",
        transformOrigin: "top center",
        mb: acknowledging ? 0 : `${gapBottom}px`,
        willChange: acknowledging ? "opacity, transform, max-height" : "auto",
        "@keyframes boardEnter": {
          "0%": {
            opacity: 0,
            transform: "translateY(-14px)",
            filter: "blur(2px)",
          },
          "100%": {
            opacity: 1,
            transform: "translateY(0)",
            filter: "blur(0)",
          },
        },
        "@keyframes ackDismiss": {
          "0%": {
            opacity: 1,
            maxHeight: 480,
            marginBottom: gapBottom,
            transform: "translateY(0) scale(1)",
          },
          "9%": {
            transform: "translateY(0) scale(1.012)",
          },
          "16%": {
            transform: "translateY(0) scale(1)",
          },
          "52%": {
            opacity: 1,
            maxHeight: 480,
            marginBottom: gapBottom,
            transform: "translateY(0) scale(1)",
          },
          "100%": {
            opacity: 0,
            maxHeight: 0,
            marginBottom: 0,
            transform: "translateY(-16px) scale(0.97)",
          },
        },
        animation: acknowledging
          ? `ackDismiss ${ACK_HOLD_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`
          : entering
            ? `boardEnter ${ENTER_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
            : "none",
      }}
    >
      {children}
    </Box>
  );
}

function AcceptedBadge({ large }: { large?: boolean }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: large ? 1.75 : 1.25,
        py: large ? 0.85 : 0.55,
        borderRadius: "2px",
        border: "1.5px solid #abefc6",
        bgcolor: "#ecfdf3",
        color: "#067647",
        fontWeight: 700,
        fontSize: large ? "1.2rem" : "1.05rem",
        "@keyframes ackBadgeIn": {
          "0%": { opacity: 0, transform: "scale(0.82) translateY(4px)" },
          "100%": { opacity: 1, transform: "scale(1) translateY(0)" },
        },
        animation: "ackBadgeIn 420ms cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          display: "inline-flex",
          width: large ? 22 : 18,
          height: large ? 22 : 18,
          borderRadius: "50%",
          bgcolor: "#067647",
          color: "#fff",
          fontSize: large ? "0.85rem" : "0.75rem",
          fontWeight: 800,
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 1,
          "@keyframes ackCheckPop": {
            "0%": { transform: "scale(0)" },
            "60%": { transform: "scale(1.15)" },
            "100%": { transform: "scale(1)" },
          },
          animation:
            "ackCheckPop 480ms cubic-bezier(0.22, 1, 0.36, 1) 80ms both",
        }}
      >
        ✓
      </Box>
      Accepted
    </Box>
  );
}

/** Light, sharp, TV-scale theme — scoped to this page only. */
const tvDashboardTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#1a4f8c" },
    error: { main: "#b42318" },
    warning: { main: "#b54708" },
    success: { main: "#067647" },
    info: { main: "#175cd3" },
    background: {
      default: "#e8ecf1",
      paper: "#ffffff",
    },
    text: {
      primary: "#121926",
      secondary: "#4b5565",
    },
    divider: "#c8d0dc",
  },
  typography: {
    fontFamily: 'var(--font-sans), "Segoe UI", sans-serif',
    h3: { fontWeight: 700, letterSpacing: "-0.02em" },
    h4: { fontWeight: 700, letterSpacing: "-0.01em" },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    body1: { fontSize: "1.25rem", lineHeight: 1.35 },
    body2: { fontSize: "1.125rem", lineHeight: 1.35 },
    subtitle1: { fontSize: "1.35rem", fontWeight: 700, lineHeight: 1.3 },
    subtitle2: { fontSize: "1.2rem", fontWeight: 700, lineHeight: 1.3 },
    caption: { fontSize: "1rem", lineHeight: 1.3 },
    overline: {
      fontSize: "0.95rem",
      fontWeight: 700,
      letterSpacing: "0.14em",
    },
  },
  shape: { borderRadius: 2 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#e8ecf1",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 2, backgroundColor: "#fff" },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: { fontSize: "1.15rem" },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: "1.1rem" },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontSize: "1.15rem", borderRadius: 0 },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          fontSize: "1.15rem",
          minWidth: 44,
          height: 44,
        },
      },
    },
  },
});

export type DashboardRequest = {
  patientRequestId: string;
  tenantKey: string;
  requestCode: string;
  requestTitle: string | null;
  patientTranscript: string | null;
  status: string;
  currentTierOrder: number;
  escalationRoleCode: string | null;
  escalationTierName: string | null;
  createdDateTimeUtc: string;
  updatedDateTimeUtc: string;
  deviceId: string;
  deviceName: string | null;
  organizationName: string | null;
  siteName: string | null;
  locationName: string | null;
  room: string | null;
  bed: string | null;
  priority: string | null;
};

type FilterOption = { id: number; name: string; parentId: number | null };

type PriorityFilterOption = { value: string; label: string };

type DashboardResponse = {
  totalCount?: number;
  TotalCount?: number;
  totalPages?: number;
  TotalPages?: number;
  showOrganization?: boolean;
  ShowOrganization?: boolean;
  items?: Array<Record<string, unknown>>;
  Items?: Array<Record<string, unknown>>;
};

type StatusTone = "neutral" | "ok" | "warn" | "bad" | "info";

function normalizeItem(raw: Record<string, unknown>): DashboardRequest {
  const str = (a: string, b: string) => String(raw[a] ?? raw[b] ?? "");
  const strOrNull = (a: string, b: string) => {
    const v = raw[a] ?? raw[b];
    return v == null || v === "" ? null : String(v);
  };
  const num = (a: string, b: string) => Number(raw[a] ?? raw[b] ?? 0);

  return {
    patientRequestId: str("patientRequestId", "PatientRequestId"),
    tenantKey: str("tenantKey", "TenantKey"),
    requestCode: str("requestCode", "RequestCode"),
    requestTitle: strOrNull("requestTitle", "RequestTitle"),
    patientTranscript: strOrNull("patientTranscript", "PatientTranscript"),
    status: str("status", "Status"),
    currentTierOrder: num("currentTierOrder", "CurrentTierOrder"),
    escalationRoleCode: strOrNull("escalationRoleCode", "EscalationRoleCode"),
    escalationTierName: strOrNull("escalationTierName", "EscalationTierName"),
    createdDateTimeUtc: str("createdDateTimeUtc", "CreatedDateTimeUtc"),
    updatedDateTimeUtc: str("updatedDateTimeUtc", "UpdatedDateTimeUtc"),
    deviceId: str("deviceId", "DeviceId"),
    deviceName: strOrNull("deviceName", "DeviceName"),
    organizationName: strOrNull("organizationName", "OrganizationName"),
    siteName: strOrNull("siteName", "SiteName"),
    locationName: strOrNull("locationName", "LocationName"),
    room: strOrNull("room", "Room"),
    bed: strOrNull("bed", "Bed"),
    priority: strOrNull("priority", "Priority"),
  };
}

function normalizeFilter(raw: Record<string, unknown>): FilterOption {
  const id = Number(raw.id ?? raw.Id ?? 0);
  const parent = raw.parentId ?? raw.ParentId;
  return {
    id,
    name: String(raw.name ?? raw.Name ?? ""),
    parentId: parent == null || parent === "" ? null : Number(parent),
  };
}

function normalizePriorityFilter(
  raw: Record<string, unknown>,
): PriorityFilterOption {
  return {
    value: String(raw.value ?? raw.Value ?? "").trim(),
    label: String(raw.label ?? raw.Label ?? "").trim(),
  };
}

function isEmergency(item: DashboardRequest): boolean {
  return item.requestCode.toUpperCase() === "EMERGENCY";
}

/** Staff-closed — timer freezes; Acknowledge disabled. EscalationExhausted stays open. */
function isClosedByStaff(status: string): boolean {
  switch (status.toLowerCase()) {
    case "accepted":
    case "completed":
    case "cancelled":
      return true;
    default:
      return false;
  }
}

function canAcknowledge(status: string): boolean {
  return !isClosedByStaff(status);
}

function liveStatusLabel(status: AblyLiveStatus): string {
  return status === "online" ? "Online" : "Offline";
}

function LiveStatusIndicator({ status }: { status: AblyLiveStatus }) {
  const online = status === "online";

  return (
    <Chip
      size="small"
      label={liveStatusLabel(status)}
      color={online ? "success" : "error"}
      title={
        online
          ? "Connected to live board updates"
          : "Live updates offline — board refreshes periodically"
      }
      sx={{
        height: 22,
        fontWeight: 700,
        fontSize: "0.8rem",
        letterSpacing: "0.04em",
        alignSelf: "center",
      }}
    />
  );
}

/**
 * Board status — short and friendly.
 * First notify = Notified. Only after a tier advance = Escalated (+ count).
 */
function formatBoardStatus(item: DashboardRequest): string {
  switch (item.status.toLowerCase()) {
    case "pendingnotify":
      return "Sending";
    case "notifyfailed":
      return "Notify failed";
    case "accepted":
      return "Accepted";
    case "completed":
      return "Completed";
    case "cancelled":
      return "Cancelled";
    case "escalationexhausted":
      return "Needs attention";
    case "notified":
      // Still on the first tier — not escalated yet.
      if (item.currentTierOrder <= 1) return "Notified";
      return "Escalated";
    case "escalated":
      return "Escalated";
    default: {
      const spaced = item.status
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/_/g, " ")
        .trim();
      if (!spaced) return item.status;
      return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
    }
  }
}

/** Tier 2 => 1 escalation, tier 3 => 2, … */
function escalationCount(item: DashboardRequest): number | null {
  const status = item.status.toLowerCase();
  if (status === "escalated") {
    return Math.max(1, item.currentTierOrder - 1);
  }
  if (status === "notified" && item.currentTierOrder > 1) {
    return Math.max(1, item.currentTierOrder - 1);
  }
  return null;
}

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "accepted":
    case "completed":
      return "ok";
    case "pendingnotify":
    case "notified":
      return "warn";
    case "escalated":
    case "notifyfailed":
    case "escalationexhausted":
      return "bad";
    case "cancelled":
      return "neutral";
    default:
      return "info";
  }
}

function formatElapsed(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m ${String(seconds).padStart(2, "0")}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  }
  return `${seconds}s`;
}

/** Live tick shared across the board so every row stays in sync. */
function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

function parseUtcMs(iso: string): number {
  const raw = iso.trim();
  if (!raw) return Number.NaN;
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(raw);
  return new Date(hasZone ? raw : `${raw}Z`).getTime();
}

function openDurationMs(item: DashboardRequest, now: number): number {
  const start = parseUtcMs(item.createdDateTimeUtc);
  if (Number.isNaN(start)) return 0;
  // Only freeze after staff closes it; exhausted escalations keep counting.
  if (isClosedByStaff(item.status)) {
    const end = parseUtcMs(item.updatedDateTimeUtc);
    if (!Number.isNaN(end) && end >= start) return end - start;
  }
  return Math.max(0, now - start);
}

function OpenForLabel({ item, now }: { item: DashboardRequest; now: number }) {
  const open = !isClosedByStaff(item.status);
  return (
    <Typography
      component="time"
      dateTime={item.createdDateTimeUtc}
      sx={{
        display: "block",
        fontSize: "1.35rem",
        color: open ? "#121926" : "text.secondary",
        fontWeight: 800,
        fontVariantNumeric: "tabular-nums",
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
        fontFamily: "var(--font-mono), ui-monospace, monospace",
      }}
    >
      {formatElapsed(openDurationMs(item, now))}
    </Typography>
  );
}

function roomBedLabel(item: DashboardRequest): string {
  const roomBed = [
    item.room && `Room ${item.room}`,
    item.bed && `Bed ${item.bed}`,
  ]
    .filter(Boolean)
    .join(" · ");
  if (roomBed) return roomBed;
  return item.deviceName || `Device ${item.deviceId || "unknown"}`;
}

function placeParts(
  item: DashboardRequest,
  showOrganization: boolean,
): string[] {
  const parts: string[] = [];
  if (showOrganization && item.organizationName) {
    parts.push(item.organizationName);
  }
  if (item.siteName) parts.push(item.siteName);
  if (item.locationName) parts.push(item.locationName);
  return parts;
}

/** Full breadcrumb for tooltips. */
function placeFullPath(
  item: DashboardRequest,
  showOrganization: boolean,
): string {
  return placeParts(item, showOrganization).join(" · ");
}

/** Org / site / location each on its own line (no wrap). */
function LocationLines({
  item,
  showOrganization,
  large,
}: {
  item: DashboardRequest;
  showOrganization: boolean;
  large?: boolean;
}) {
  const parts = placeParts(item, showOrganization);
  if (parts.length === 0) {
    return (
      <Typography
        sx={{
          fontSize: large ? "1.25rem" : "1.15rem",
          color: "text.secondary",
        }}
      >
        —
      </Typography>
    );
  }

  return (
    <Stack
      spacing={0.1}
      title={placeFullPath(item, showOrganization)}
      sx={{ minWidth: 0 }}
    >
      {parts.map((part, index) => {
        const isLeaf = index === parts.length - 1;
        return (
          <Typography
            key={`${part}-${index}`}
            noWrap
            sx={{
              minWidth: 0,
              fontSize: large
                ? isLeaf
                  ? "1.25rem"
                  : "1.05rem"
                : isLeaf
                  ? "1.1rem"
                  : "0.98rem",
              fontWeight: isLeaf ? 700 : 500,
              color: isLeaf ? "text.primary" : "text.secondary",
              lineHeight: 1.25,
            }}
          >
            {part}
          </Typography>
        );
      })}
    </Stack>
  );
}

const STATUS_STYLES: Record<
  StatusTone,
  { bg: string; color: string; border: string }
> = {
  ok: { bg: "#ecfdf3", color: "#067647", border: "#abefc6" },
  warn: { bg: "#fffaeb", color: "#b54708", border: "#fedf89" },
  bad: { bg: "#fef3f2", color: "#b42318", border: "#fecdca" },
  info: { bg: "#eff8ff", color: "#175cd3", border: "#b2ddff" },
  neutral: { bg: "#f8fafc", color: "#4b5565", border: "#d0d5dd" },
};

/** Sharp rectangular status tag — readable from TV distance. */
function StatusTag({
  item,
  large,
}: {
  item: DashboardRequest;
  large?: boolean;
}) {
  const tone = escalationCount(item) != null ? "bad" : statusTone(item.status);
  const style = STATUS_STYLES[tone];
  const count = escalationCount(item);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: count != null ? 0.75 : 0,
        px: large ? 1.75 : 1.25,
        py: large ? 0.85 : 0.55,
        borderRadius: "2px",
        border: `1.5px solid ${style.border}`,
        bgcolor: style.bg,
        color: style.color,
        fontWeight: 700,
        fontSize: large ? "1.2rem" : "1.05rem",
        letterSpacing: "0.02em",
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      {formatBoardStatus(item)}
      {count != null ? (
        <Box
          component="span"
          aria-label={`Escalated ${count} times`}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: large ? 26 : 22,
            height: large ? 26 : 22,
            px: 0.5,
            borderRadius: "2px",
            bgcolor: style.color,
            color: "#fff",
            fontSize: large ? "0.95rem" : "0.85rem",
            fontWeight: 800,
            letterSpacing: 0,
            lineHeight: 1,
          }}
        >
          {count}
        </Box>
      ) : null}
    </Box>
  );
}

function priorityTone(priority: string | null): StatusTone {
  switch ((priority ?? "").toLowerCase()) {
    case "urgent":
      return "bad";
    case "high":
      return "warn";
    case "low":
      return "neutral";
    case "routine":
    default:
      return "info";
  }
}

/** Sharp rectangular priority tag — readable from TV distance. */
function PriorityTag({
  priority,
  large,
}: {
  priority: string | null;
  large?: boolean;
}) {
  if (!priority) {
    return (
      <Typography sx={{ fontSize: large ? "1.2rem" : "1.05rem", color: "text.secondary" }}>
        —
      </Typography>
    );
  }
  const style = STATUS_STYLES[priorityTone(priority)];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: large ? 1.75 : 1.25,
        py: large ? 0.85 : 0.55,
        borderRadius: "2px",
        border: `1.5px solid ${style.border}`,
        bgcolor: style.bg,
        color: style.color,
        fontWeight: 700,
        fontSize: large ? "1.2rem" : "1.05rem",
        letterSpacing: "0.02em",
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      {priority}
    </Box>
  );
}

function RequestActionsMenu({
  item,
  onAcknowledged,
  disabled,
  tenantKey,
}: {
  item: DashboardRequest;
  onAcknowledged: (patientRequestId: string) => void;
  disabled?: boolean;
  tenantKey: string;
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [busy, setBusy] = useState(false);
  const open = Boolean(anchor);
  const enabled = canAcknowledge(item.status) && !disabled;

  return (
    <>
      <IconButton
        aria-label="Request actions"
        onClick={(e) => setAnchor(e.currentTarget)}
        disabled={busy || disabled}
        sx={{
          borderRadius: "2px",
          width: 48,
          height: 48,
        }}
      >
        <MoreVertIcon sx={{ fontSize: 28 }} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "2px",
              border: "1px solid #c8d0dc",
              minWidth: 220,
            },
          },
        }}
      >
        <MenuItem
          disabled={!enabled || busy}
          sx={{ fontSize: "1.2rem", py: 1.5, borderRadius: 0 }}
          onClick={() => {
            setAnchor(null);
            setBusy(true);
            void acceptPatientRequest(
              item.patientRequestId,
              undefined,
              tenantKey,
            )
              .then((result) => {
                if (!result.ok) {
                  window.alert(
                    result.message || "Could not acknowledge request.",
                  );
                  return;
                }
                onAcknowledged(item.patientRequestId);
              })
              .finally(() => setBusy(false));
          }}
        >
          {enabled ? "Acknowledge" : "Already closed"}
        </MenuItem>
      </Menu>
    </>
  );
}

function Surface({
  children,
  sx,
  ...rest
}: {
  children: ReactNode;
  sx?: object;
} & ComponentProps<typeof Box>) {
  return (
    <Box
      {...rest}
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "2px",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function EmergencyCard({
  item,
  showOrganization,
  onAcknowledged,
  now,
  acknowledging,
  tenantKey,
}: {
  item: DashboardRequest;
  showOrganization: boolean;
  onAcknowledged: (patientRequestId: string) => void;
  now: number;
  acknowledging: boolean;
  tenantKey: string;
}) {
  const placeTitle = placeFullPath(item, showOrganization);

  return (
    <Box
      sx={{
        bgcolor: acknowledging ? "#ecfdf3" : "#fff5f5",
        border: acknowledging ? "3px solid #067647" : "3px solid #b42318",
        borderRadius: "2px",
        borderLeftWidth: 12,
        px: { xs: 2.5, md: 4 },
        py: { xs: 2.5, md: 3.5 },
        transition:
          "background-color 320ms ease, border-color 320ms ease, box-shadow 320ms ease",
        boxShadow: acknowledging
          ? "inset 0 0 0 1px rgba(6, 118, 71, 0.12)"
          : "none",
        "@keyframes ackSuccessWash": {
          "0%": { backgroundColor: "#d1fadf" },
          "100%": { backgroundColor: "#ecfdf3" },
        },
        "@keyframes ackGlow": {
          "0%": { boxShadow: "0 0 0 0 rgba(6, 118, 71, 0.28)" },
          "100%": { boxShadow: "0 0 0 18px rgba(6, 118, 71, 0)" },
        },
        animation: acknowledging
          ? "ackSuccessWash 700ms ease-out, ackGlow 900ms ease-out"
          : "none",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        useFlexGap
        sx={{
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
        }}
      >
        <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 800,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: acknowledging ? "#067647" : "#b42318",
              fontSize: "1.05rem",
              transition: "color 280ms ease",
            }}
          >
            {acknowledging ? "Acknowledged" : "Emergency"}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: "2rem", md: "2.75rem" },
              lineHeight: 1.1,
              color: "#121926",
            }}
          >
            {roomBedLabel(item)}
          </Typography>
          {placeTitle ? (
            <Box sx={{ maxWidth: { xs: "100%", md: 360 }, minWidth: 0 }}>
              <LocationLines
                item={item}
                showOrganization={showOrganization}
                large
              />
            </Box>
          ) : null}
          {item.patientTranscript ? (
            <Typography
              sx={{
                fontSize: { xs: "1.35rem", md: "1.6rem" },
                fontStyle: "italic",
                color: "#121926",
                mt: 0.5,
              }}
            >
              “{item.patientTranscript}”
            </Typography>
          ) : null}
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{
            alignItems: "center",
            flexShrink: 0,
            alignSelf: { xs: "stretch", md: "center" },
            justifyContent: { xs: "space-between", md: "flex-end" },
            pt: { xs: 0.5, md: 0 },
            borderTop: {
              xs: acknowledging
                ? "1px solid rgba(6, 118, 71, 0.2)"
                : "1px solid rgba(180, 35, 24, 0.18)",
              md: "none",
            },
            mt: { xs: 0.5, md: 0 },
          }}
        >
          {acknowledging ? (
            <AcceptedBadge large />
          ) : (
            <StatusTag item={item} large />
          )}
          <OpenForLabel item={item} now={now} />
          <RequestActionsMenu
            item={item}
            onAcknowledged={onAcknowledged}
            disabled={acknowledging}
            tenantKey={tenantKey}
          />
        </Stack>
      </Stack>
    </Box>
  );
}

/** Shared desktop queue columns — Status needs room for “Needs attention”. */
const QUEUE_GRID_COLUMNS =
  "minmax(120px, 0.85fr) minmax(90px, 0.7fr) minmax(160px, 1.1fr) minmax(0, 1.35fr) minmax(90px, 0.55fr) minmax(175px, 0.95fr) minmax(150px, 0.8fr) 56px";

function QueueHeader() {
  return (
    <Box
      sx={{
        display: { xs: "none", md: "grid" },
        gridTemplateColumns: QUEUE_GRID_COLUMNS,
        gap: 2,
        px: 3,
        py: 1,
      }}
    >
      {[
        "Room",
        "Request",
        "Location",
        "What they said",
        "Priority",
        "Status",
        "Open for",
        "",
      ].map((label, i) => (
        <Typography
          key={`${label}-${i}`}
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#4b5565",
          }}
        >
          {label || "Actions"}
        </Typography>
      ))}
    </Box>
  );
}

function DenseRequestRow({
  item,
  showOrganization,
  onAcknowledged,
  now,
  acknowledging,
  tenantKey,
}: {
  item: DashboardRequest;
  showOrganization: boolean;
  onAcknowledged: (patientRequestId: string) => void;
  now: number;
  acknowledging: boolean;
  tenantKey: string;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: QUEUE_GRID_COLUMNS,
        },
        gap: { xs: 1, md: 2 },
        alignItems: "center",
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 2.25 },
        bgcolor: acknowledging ? "#ecfdf3" : "background.paper",
        border: acknowledging ? "1.5px solid #067647" : "1px solid",
        borderColor: acknowledging ? "#067647" : "divider",
        borderRadius: "2px",
        borderLeftWidth: acknowledging ? 6 : 4,
        borderLeftColor: acknowledging ? "#067647" : "#c8d0dc",
        transition: "background-color 320ms ease, border-color 320ms ease",
        "@keyframes ackRowWash": {
          "0%": { backgroundColor: "#d1fadf" },
          "100%": { backgroundColor: "#ecfdf3" },
        },
        animation: acknowledging ? "ackRowWash 650ms ease-out" : "none",
      }}
    >
      <Typography
        noWrap
        sx={{
          fontWeight: 800,
          fontSize: { xs: "1.4rem", md: "1.5rem" },
          minWidth: 0,
        }}
      >
        {roomBedLabel(item)}
      </Typography>
      <Typography
        noWrap
        sx={{
          fontWeight: 700,
          fontSize: { xs: "1.25rem", md: "1.35rem" },
          minWidth: 0,
        }}
      >
        {item.requestTitle || item.requestCode}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <LocationLines item={item} showOrganization={showOrganization} />
      </Box>
      <Typography
        noWrap
        sx={{
          fontSize: "1.2rem",
          color: "text.secondary",
          fontStyle: item.patientTranscript ? "italic" : "normal",
          minWidth: 0,
        }}
      >
        {item.patientTranscript ? `“${item.patientTranscript}”` : "—"}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <PriorityTag priority={item.priority} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {acknowledging ? <AcceptedBadge /> : <StatusTag item={item} />}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <OpenForLabel item={item} now={now} />
      </Box>
      <Box sx={{ justifySelf: { xs: "start", md: "end" } }}>
        <RequestActionsMenu
          item={item}
          onAcknowledged={onAcknowledged}
          disabled={acknowledging}
          tenantKey={tenantKey}
        />
      </Box>
    </Box>
  );
}

export function RequestsDashboard() {
  const { userProfile, isLoading: authLoading } = useAuth();
  const tenantKey =
    managementTenantKey() || userProfile?.profile.tenantKey?.trim() || "";
  const now = useNow(1000);
  const [page, setPage] = useState(1);
  const [orgId, setOrgId] = useState(ALL);
  const [siteId, setSiteId] = useState(ALL);
  const [locationId, setLocationId] = useState(ALL);
  const [priorityId, setPriorityId] = useState(ALL);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filtersActivity, setFiltersActivity] = useState(0);
  const [tvMode, setTvMode] = useState(readTvModePreference);
  /** Do not paint the board until the first availability check finishes. */
  const [access, setAccess] = useState<"loading" | "ready" | "unavailable">(
    "loading",
  );
  const filtersActive =
    orgId !== ALL ||
    siteId !== ALL ||
    locationId !== ALL ||
    priorityId !== ALL;

  useEffect(() => {
    setStaffTenantFallback(userProfile?.profile.tenantKey);
  }, [userProfile?.profile.tenantKey]);

  useEffect(() => {
    try {
      window.localStorage.setItem(TV_MODE_STORAGE_KEY, tvMode ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [tvMode]);

  const bumpFiltersActivity = useCallback(() => {
    setFiltersActivity((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!filtersOpen) return;
    const id = window.setTimeout(() => {
      setFiltersOpen(false);
    }, FILTERS_IDLE_MS);
    return () => window.clearTimeout(id);
  }, [filtersOpen, filtersActivity, orgId, siteId, locationId, priorityId]);

  const [organizations, setOrganizations] = useState<FilterOption[]>([]);
  const [sites, setSites] = useState<FilterOption[]>([]);
  const [locations, setLocations] = useState<FilterOption[]>([]);
  const [priorities, setPriorities] = useState<PriorityFilterOption[]>([]);
  const [showOrganization, setShowOrganization] = useState(false);

  const [items, setItems] = useState<DashboardRequest[]>([]);
  const [acknowledgingIds, setAcknowledgingIds] = useState<
    Record<string, true>
  >({});
  const acknowledgingIdsRef = useRef(acknowledgingIds);
  acknowledgingIdsRef.current = acknowledgingIds;
  const [enteringIds, setEnteringIds] = useState<Record<string, true>>({});
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const prevLiveStatusRef = useRef<AblyLiveStatus | null>(null);

  const beginAcknowledge = useCallback((patientRequestId: string) => {
    const id = String(patientRequestId);
    setAcknowledgingIds((prev) => {
      if (prev[id]) return prev;
      return { ...prev, [id]: true };
    });
    setItems((prev) =>
      prev.map((item) =>
        item.patientRequestId === id ? { ...item, status: "Accepted" } : item,
      ),
    );
    window.setTimeout(() => {
      setItems((prev) => prev.filter((item) => item.patientRequestId !== id));
      setAcknowledgingIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }, ACK_HOLD_MS);
  }, []);

  const loadFilters = useCallback(
    async (org: string, site: string) => {
      if (!tenantKey) {
        if (!authLoading) setAccess("unavailable");
        return;
      }
      const params = new URLSearchParams();
      params.set("tenantKey", tenantKey);
      if (org !== ALL) params.set("organizationId", org);
      if (site !== ALL) params.set("organizationSiteId", site);
      const res = await fetchManagementJson(`location-filters?${params}`);
      if (res.status === 404 || res.status === 400) {
        setAccess("unavailable");
        return;
      }
      if (!res.ok) throw new Error(`Failed to load filters (${res.status})`);
      const body = (await res.json()) as {
        showOrganization?: boolean;
        ShowOrganization?: boolean;
        organizations?: Array<Record<string, unknown>>;
        Organizations?: Array<Record<string, unknown>>;
        sites?: Array<Record<string, unknown>>;
        Sites?: Array<Record<string, unknown>>;
        locations?: Array<Record<string, unknown>>;
        Locations?: Array<Record<string, unknown>>;
        priorities?: Array<Record<string, unknown>>;
        Priorities?: Array<Record<string, unknown>>;
      };
      setShowOrganization(
        Boolean(body.showOrganization ?? body.ShowOrganization),
      );
      setOrganizations(
        (body.organizations ?? body.Organizations ?? []).map(normalizeFilter),
      );
      setSites((body.sites ?? body.Sites ?? []).map(normalizeFilter));
      setLocations(
        (body.locations ?? body.Locations ?? []).map(normalizeFilter),
      );
      setPriorities(
        (body.priorities ?? body.Priorities ?? [])
          .map(normalizePriorityFilter)
          .filter((p) => p.value && p.label),
      );
    },
    [tenantKey, authLoading],
  );

  const loadRequests = useCallback(
    async (
      pageNum: number,
      org: string,
      site: string,
      location: string,
      priority: string,
      silent = false,
    ) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        if (!tenantKey) {
          if (!authLoading) setAccess("unavailable");
          return;
        }
        const params = new URLSearchParams({
          page: String(pageNum),
          pageSize: String(PAGE_SIZE),
          tenantKey,
        });
        if (org !== ALL) params.set("organizationId", org);
        if (site !== ALL) params.set("organizationSiteId", site);
        if (location !== ALL)
          params.set("organizationSiteLocationId", location);
        if (priority !== ALL) params.append("priority", priority);

        const res = await fetchManagementJson(`requests?${params}`);
        if (!res.ok) {
          if (res.status === 401) {
            throw new Error("Session expired. Please sign in again.");
          }
          if (res.status === 404 || res.status === 400) {
            setAccess("unavailable");
            return;
          }
          throw new Error(`Failed to load requests (${res.status})`);
        }
        setAccess("ready");
        const body = (await res.json()) as DashboardResponse;
        const rawItems = body.items ?? body.Items ?? [];
        const nextItems = rawItems.map(normalizeItem);
        const ackIds = acknowledgingIdsRef.current;

        setItems((prev) => {
          const nextIds = new Set(nextItems.map((i) => i.patientRequestId));
          // Keep rows mid-ack animation even though the API no longer returns them.
          const preserved = prev.filter(
            (item) =>
              ackIds[item.patientRequestId] &&
              !nextIds.has(item.patientRequestId),
          );

          if (silent) {
            const prevOpenIds = new Set(
              prev
                .filter((i) => !ackIds[i.patientRequestId])
                .map((i) => i.patientRequestId),
            );
            const newcomers = nextItems
              .filter((i) => !prevOpenIds.has(i.patientRequestId))
              .map((i) => i.patientRequestId);
            if (newcomers.length > 0) {
              queueMicrotask(() => {
                setEnteringIds((e) => {
                  const merged = { ...e };
                  for (const id of newcomers) merged[id] = true;
                  return merged;
                });
                window.setTimeout(() => {
                  setEnteringIds((e) => {
                    const cleaned = { ...e };
                    for (const id of newcomers) delete cleaned[id];
                    return cleaned;
                  });
                }, ENTER_MS + 80);
              });
            }
          }

          // Keep API order; append mid-ack rows that the API already dropped.
          return [...nextItems, ...preserved];
        });
        setTotalCount(body.totalCount ?? body.TotalCount ?? 0);
        setTotalPages(body.totalPages ?? body.TotalPages ?? 0);
        setShowOrganization(
          Boolean(body.showOrganization ?? body.ShowOrganization),
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        // Don't leave the gate stuck on the spinner for non-404 failures.
        if (!silent) {
          setAccess((prev) => (prev === "loading" ? "ready" : prev));
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [tenantKey, authLoading],
  );

  const refreshSilent = useCallback(() => {
    void loadRequests(page, orgId, siteId, locationId, priorityId, true);
  }, [loadRequests, page, orgId, siteId, locationId, priorityId]);

  const liveStatus = useConciergeBoardAbly({
    tenantKey,
    enabled: access === "ready",
    onCreated: () => {
      // Source of truth stays the API so every board session matches.
      refreshSilent();
    },
    onAcknowledged: (payload) => {
      const id = String(
        payload.patientRequestId ?? payload.PatientRequestId ?? "",
      );
      if (id) beginAcknowledge(id);
      // After dismiss animation, sync totals/page from API (accepted are excluded).
      window.setTimeout(() => refreshSilent(), ACK_HOLD_MS + 50);
    },
    onEscalated: (payload) => {
      const id = String(
        payload.patientRequestId ?? payload.PatientRequestId ?? "",
      );
      if (id) {
        setItems((prev) =>
          prev.map((item) => {
            if (item.patientRequestId !== id) return item;
            return {
              ...item,
              status: String(payload.status ?? payload.Status ?? item.status),
              currentTierOrder: Number(
                payload.currentTierOrder ??
                  payload.CurrentTierOrder ??
                  item.currentTierOrder,
              ),
              escalationRoleCode:
                payload.escalationRoleCode ??
                payload.EscalationRoleCode ??
                item.escalationRoleCode,
              escalationTierName:
                payload.escalationTierName ??
                payload.EscalationTierName ??
                item.escalationTierName,
              updatedDateTimeUtc: String(
                payload.updatedDateTimeUtc ??
                  payload.UpdatedDateTimeUtc ??
                  item.updatedDateTimeUtc,
              ),
            };
          }),
        );
      }
      refreshSilent();
    },
  });

  useEffect(() => {
    if (authLoading) return;
    if (access === "unavailable") return;
    void loadFilters(orgId, siteId).catch((err) => {
      setError(err instanceof Error ? err.message : String(err));
    });
  }, [access, authLoading, loadFilters, orgId, siteId]);

  useEffect(() => {
    if (authLoading) return;
    void loadRequests(page, orgId, siteId, locationId, priorityId);
  }, [authLoading, loadRequests, page, orgId, siteId, locationId, priorityId]);

  // Poll only when live updates are offline.
  useEffect(() => {
    if (access !== "ready") return;
    if (liveStatus === "online") return;
    const id = window.setInterval(() => {
      void loadRequests(page, orgId, siteId, locationId, priorityId, true);
    }, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [
    access,
    liveStatus,
    loadRequests,
    page,
    orgId,
    siteId,
    locationId,
    priorityId,
  ]);

  // One sync when live comes back online.
  useEffect(() => {
    if (access !== "ready") return;
    const prev = prevLiveStatusRef.current;
    prevLiveStatusRef.current = liveStatus;
    if (liveStatus === "online" && prev && prev !== "online") {
      refreshSilent();
    }
  }, [access, liveStatus, refreshSilent]);

  if (authLoading || access === "loading") {
    return <ConciergeChecking />;
  }

  if (access === "unavailable") {
    return <ConciergeUnavailable />;
  }

  return (
    <ThemeProvider theme={tvDashboardTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100dvh",
          py: { xs: 3, md: 4 },
          bgcolor: boardAmbientBg(tvMode, liveStatus),
          color: "text.primary",
          display: "flex",
          flexDirection: "column",
          transition: "background-color 0.8s ease",
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            px: { xs: 2, md: 4, xl: 5 },
            maxWidth: 1800,
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack spacing={3} sx={{ flex: 1, minHeight: 0 }}>
            <StaffAppHeader
              title="Patient Concierge Board"
              active="board"
              leading={
                <Chip
                  size="small"
                  label={String(totalCount)}
                  color={totalCount > 0 ? "error" : "default"}
                  aria-label={`${totalCount} open requests`}
                  sx={{
                    height: 22,
                    minWidth: 22,
                    fontWeight: 800,
                    fontSize: "0.85rem",
                    flexShrink: 0,
                  }}
                />
              }
              trailing={<LiveStatusIndicator status={liveStatus} />}
              actions={
                <>
                  <HeaderIconTooltip
                    title={tvMode ? "TV mode on" : "TV mode off"}
                  >
                    <IconButton
                      aria-label={
                        tvMode ? "Turn off TV mode" : "Turn on TV mode"
                      }
                      aria-pressed={tvMode}
                      onClick={() => setTvMode((v) => !v)}
                      size="small"
                      sx={{
                        color: tvMode ? "text.primary" : "text.secondary",
                        opacity: tvMode ? 0.75 : 0.45,
                        "&:hover": {
                          opacity: 0.9,
                          bgcolor: "transparent",
                        },
                      }}
                    >
                      <LiveTvOutlinedIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                  </HeaderIconTooltip>
                  <HeaderIconTooltip
                    title={filtersOpen ? "Hide filters" : "Filters"}
                  >
                    <IconButton
                      aria-label={filtersOpen ? "Hide filters" : "Show filters"}
                      aria-expanded={filtersOpen}
                      onClick={() => {
                        setFiltersOpen((o) => {
                          const next = !o;
                          if (next) setFiltersActivity((n) => n + 1);
                          return next;
                        });
                      }}
                      size="small"
                      sx={{
                        color:
                          filtersOpen || filtersActive
                            ? "text.primary"
                            : "text.secondary",
                        opacity: filtersOpen || filtersActive ? 0.75 : 0.45,
                        position: "relative",
                        "&:hover": {
                          opacity: 0.9,
                          bgcolor: "transparent",
                        },
                      }}
                    >
                      <FilterListIcon sx={{ fontSize: 22 }} />
                      {filtersActive ? (
                        <Box
                          component="span"
                          sx={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            bgcolor: "#1a4f8c",
                          }}
                        />
                      ) : null}
                    </IconButton>
                  </HeaderIconTooltip>
                </>
              }
            />

            {filtersOpen ? (
              <Surface
                sx={{ p: 2 }}
                onPointerDown={bumpFiltersActivity}
                onFocusCapture={bumpFiltersActivity}
                onKeyDown={bumpFiltersActivity}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  useFlexGap
                >
                  {showOrganization && (
                    <FormControl sx={{ minWidth: 160, flex: 1 }} size="small">
                      <InputLabel id="org-filter-label">
                        Organization
                      </InputLabel>
                      <Select
                        labelId="org-filter-label"
                        label="Organization"
                        value={orgId}
                        onChange={(e) => {
                          setOrgId(String(e.target.value));
                          setSiteId(ALL);
                          setLocationId(ALL);
                          setPage(1);
                        }}
                      >
                        <MenuItem value={ALL}>All organizations</MenuItem>
                        {organizations.map((o) => (
                          <MenuItem key={o.id} value={String(o.id)}>
                            {o.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <FormControl sx={{ minWidth: 160, flex: 1 }} size="small">
                    <InputLabel id="site-filter-label">Site</InputLabel>
                    <Select
                      labelId="site-filter-label"
                      label="Site"
                      value={siteId}
                      onChange={(e) => {
                        setSiteId(String(e.target.value));
                        setLocationId(ALL);
                        setPage(1);
                      }}
                    >
                      <MenuItem value={ALL}>All sites</MenuItem>
                      {sites.map((s) => (
                        <MenuItem key={s.id} value={String(s.id)}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 160, flex: 1 }} size="small">
                    <InputLabel id="location-filter-label">Location</InputLabel>
                    <Select
                      labelId="location-filter-label"
                      label="Location"
                      value={locationId}
                      onChange={(e) => {
                        setLocationId(String(e.target.value));
                        setPage(1);
                      }}
                    >
                      <MenuItem value={ALL}>All locations</MenuItem>
                      {locations.map((l) => (
                        <MenuItem key={l.id} value={String(l.id)}>
                          {l.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 160, flex: 1 }} size="small">
                    <InputLabel id="priority-filter-label">Priority</InputLabel>
                    <Select
                      labelId="priority-filter-label"
                      label="Priority"
                      value={priorityId}
                      onChange={(e) => {
                        setPriorityId(String(e.target.value));
                        setPage(1);
                      }}
                    >
                      <MenuItem value={ALL}>All priorities</MenuItem>
                      {priorities.map((p) => (
                        <MenuItem key={p.value} value={p.value}>
                          {p.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Surface>
            ) : null}

            {error && (
              <Surface sx={{ p: 3, borderColor: "error.main", borderWidth: 2 }}>
                <Typography color="error" sx={{ fontSize: "1.25rem" }}>
                  {error}
                </Typography>
              </Surface>
            )}

            {!loading && items.length === 0 && !error && (
              <Stack
                spacing={2}
                sx={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  px: 2,
                  textAlign: "center",
                  minHeight: 0,
                }}
              >
                <CheckIcon
                  sx={{
                    fontSize: { xs: 64, md: 80 },
                    color: "#067647",
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.75rem", md: "2.25rem" },
                    color: "text.primary",
                    letterSpacing: "-0.02em",
                  }}
                >
                  You're all caught up
                </Typography>
              </Stack>
            )}

            {items.length > 0 && (
              <Stack spacing={1.25}>
                {items.map((item, index) => {
                  const emergency = isEmergency(item);
                  const showQueueHeader =
                    !emergency &&
                    (index === 0 || isEmergency(items[index - 1]!));
                  return (
                    <Fragment key={item.patientRequestId}>
                      {showQueueHeader ? <QueueHeader /> : null}
                      <BoardItemMotion
                        acknowledging={Boolean(
                          acknowledgingIds[item.patientRequestId],
                        )}
                        entering={Boolean(enteringIds[item.patientRequestId])}
                        gapBottom={emergency ? 16 : undefined}
                      >
                        {emergency ? (
                          <EmergencyCard
                            item={item}
                            showOrganization={showOrganization}
                            onAcknowledged={beginAcknowledge}
                            now={now}
                            acknowledging={Boolean(
                              acknowledgingIds[item.patientRequestId],
                            )}
                            tenantKey={tenantKey}
                          />
                        ) : (
                          <DenseRequestRow
                            item={item}
                            showOrganization={showOrganization}
                            onAcknowledged={beginAcknowledge}
                            now={now}
                            acknowledging={Boolean(
                              acknowledgingIds[item.patientRequestId],
                            )}
                            tenantKey={tenantKey}
                          />
                        )}
                      </BoardItemMotion>
                    </Fragment>
                  );
                })}
              </Stack>
            )}

            {totalPages > 1 && (
              <Stack sx={{ alignItems: "center", pt: 1 }}>
                <Pagination
                  color="primary"
                  page={page}
                  count={totalPages}
                  size="large"
                  onChange={(_, value) => setPage(value)}
                />
              </Stack>
            )}
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
